interface ApiRequestOptions extends RequestInit {
  params?: Record<string, string>;
  requireAuth?: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly errorCode: string | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface QueuedRequest<T> {
  endpoint: string;
  options: ApiRequestOptions;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
}

export class ApiService {
  private baseUrl: string;
  private accessToken: string | null = null;
  private authReady: boolean = false;
  private requestQueue: QueuedRequest<unknown>[] = [];

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_BACKEND_API_URL}/api/v1`;
  }

  public setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public setAuthReady(isAuthenticated: boolean): void {
    this.authReady = true;

    if (isAuthenticated) {
      this.processRequestQueue();
    } else {
      this.clearQueue();
    }
  }

  private clearQueue(): void {
    this.requestQueue.forEach((queued) => {
      queued.reject(new Error("Not authenticated"));
    });
    this.requestQueue = [];
  }

  private async processRequestQueue(): Promise<void> {
    const queue = [...this.requestQueue];
    this.requestQueue = [];

    for (const queued of queue) {
      try {
        const result = await this.executeRequest(
          queued.endpoint,
          queued.options,
        );
        queued.resolve(result);
      } catch (error) {
        queued.reject(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    }
  }

  public async request<T>(
    endpoint: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    const requireAuth = options.requireAuth !== false;

    if (requireAuth && !this.authReady) {
      return new Promise<T>((resolve, reject) => {
        this.requestQueue.push({
          endpoint,
          options,
          resolve: resolve as (value: unknown) => void,
          reject,
        });
      });
    }

    return this.executeRequest<T>(endpoint, options);
  }

  private async executeRequest<T>(
    endpoint: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    const { params, requireAuth = false, ...fetchOptions } = options;

    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...fetchOptions.headers,
    };

    if (requireAuth && this.accessToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${this.accessToken}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: "Request failed" }));
      throw new ApiError(
        body.message || body.error || `HTTP ${response.status}`,
        response.status,
        body.error || null,
      );
    }

    if (
      response.status === 204 ||
      response.headers.get("content-length") === "0"
    ) {
      return undefined as T;
    }

    return response.json();
  }
}

export const apiService = new ApiService();
